/**
 * ════════════════════════════════════════════════════════════════════════════════
 *  액세스토큰 페이로드 읽기
 * ════════════════════════════════════════════════════════════════════════════════
 *  [왜 프론트가 토큰을 들여다보는가]
 *   "내가 지금 들고 있는 토큰이 관리자용인가, 만료됐나" 를 판단하기 위해서다.
 *   보안 판단이 아니다. 보안은 전적으로 서버가 한다(서명 검증 · 영역 검증 · 권한 검증).
 *   사용자가 페이로드를 위조해봐야 서버가 거부하므로 자기 자신을 속이는 것에 불과하다.
 *
 *  [왜 필요해졌는가 — 실제 사고]
 *   관리자 재발급 창구에 사용자 RT 가 실려가 '사용자 토큰' 이 발급됐고, 프론트가
 *   그것을 검증 없이 localStorage.adminToken 에 저장했다. 라우트 가드도 '토큰이
 *   있는지' 만 봤기 때문에 관리자 화면이 그대로 열렸고, 관리자 채팅이 사용자 권한으로
 *   전송되었다. 그래서 '저장하기 전에' 와 '화면에 들어가기 전에' 두 번 확인한다.
 *
 *  서버가 토큰에 심는 클레임
 *    id   : 계정(이메일)
 *    role : '0'(사용자) / '1'(관리자)
 *    scp  : 'user' / 'admin'   <- 영역. 이 값이 핵심이다
 *    exp  : 만료시각(초)
 * ════════════════════════════════════════════════════════════════════════════════
 */

export const SCOPE_ADMIN = 'admin';
export const SCOPE_USER = 'user';

/** 'Bearer xxx' 에서 토큰 본문만 떼어낸다 */
const stripBearer = (token) => {
    const raw = String(token || '').trim();
    return raw.toLowerCase().startsWith('bearer ') ? raw.slice(7).trim() : raw;
};

/**
 * JWT 페이로드를 객체로 읽는다. 읽을 수 없으면 null.
 *
 * <p>base64url 디코딩만 한다(서명 검증 아님). 한글 등 멀티바이트를 위해 UTF-8 로 푼다.
 */
export const readTokenClaims = (token) => {
    const jwt = stripBearer(token);
    if (!jwt || jwt === 'null' || jwt === 'undefined') return null;

    const parts = jwt.split('.');
    if (parts.length < 2) return null;

    try {
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
        const binary = window.atob(padded);
        /*
         * atob 는 '바이트 하나 = 문자 하나' 인 문자열을 돌려주므로 UTF-8 로 다시 해석해야
         * 한글이 깨지지 않는다. TextDecoder 를 쓰지 않는 이유는 테스트 환경(jsdom)에
         * 전역으로 없는 경우가 있어서다. 그때 해석이 통째로 실패해 '영역을 읽을 수 없음'
         * 으로 판정되고, 멀쩡한 세션이 정리되어 버린다.
         * 아래 방식은 브라우저·jsdom 어디서나 동작한다.
         */
        const json = decodeURIComponent(
            binary
                .split('')
                .map((ch) => `%${`00${ch.charCodeAt(0).toString(16)}`.slice(-2)}`)
                .join('')
        );
        const claims = JSON.parse(json);
        return claims && typeof claims === 'object' ? claims : null;
    } catch (e) {
        return null;
    }
};

/**
 * 토큰의 영역.
 *
 *  @returns 'admin' | 'user' | null
 *
 *  [전환기] 배포 전에 발급된 토큰에는 scp 클레임이 없다.
 *   그 경우 role 로 추론한다. ('1' = 관리자)
 *   둘 다 없으면 null 을 돌려주고, 호출부가 '판단 불가' 로 다룬다.
 */
export const getTokenScope = (token) => {
    const claims = readTokenClaims(token);
    if (!claims) return null;
    if (claims.scp === SCOPE_ADMIN || claims.scp === SCOPE_USER) return claims.scp;
    if (claims.role === '1' || claims.role === 1) return SCOPE_ADMIN;
    if (claims.role === '0' || claims.role === 0) return SCOPE_USER;
    return null;
};

/** 토큰의 계정(이메일). 없으면 null */
export const getTokenId = (token) => {
    const claims = readTokenClaims(token);
    const id = claims?.id;
    return typeof id === 'string' && id ? id : null;
};

/**
 * 토큰이 만료됐는지.
 *
 *  @param skewMs 만료 직전을 '이미 만료' 로 볼 여유시간 (기본 10초).
 *                요청이 서버에 닿는 사이에 만료되는 경계를 피한다.
 *  @returns true = 만료(또는 exp 를 읽을 수 없음)
 */
export const isTokenExpired = (token, skewMs = 10_000) => {
    const claims = readTokenClaims(token);
    const exp = claims?.exp;
    if (typeof exp !== 'number') return true;      // 읽을 수 없으면 만료로 간주(안전한 쪽)
    return Date.now() + skewMs >= exp * 1000;
};

/**
 * 이 토큰을 '이 영역' 에서 써도 되는지.
 *
 *  @param token         검사할 액세스토큰
 *  @param expectedScope 'admin' | 'user'
 *  @returns true = 영역이 일치한다
 *
 *  영역을 읽을 수 없으면(깨진 토큰 등) false 다. 모르면 쓰지 않는다.
 */
export const isScopeMatched = (token, expectedScope) =>
    getTokenScope(token) === expectedScope;
